import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { civilToday, isCivilDate, links, resolveFormattingLocale, type CivilDate } from '@eis/core';
import { ErrorState, FormSection, PageHeader, PageLayout, cardSurfaceSx } from '@eis/patterns';
import { useClaimPartnerRoles, useCoverageAtDate, useCreateClaim } from '../api/claims';
import {
  LIABLE_ROLE_NAME,
  LOOKUP_MODES,
  ambiguousCodesOf,
  categoryLabel,
  createClaimSchema,
  offerableCategories,
  policyObjectCodeOf,
  policyReference,
  toCreateRequest,
  verdictOf,
  type Coverage,
  type CreateClaimValues,
  type LookupMode,
  type PolicyReference,
} from '../model/claims';
import { CoveragePanel } from './CoveragePanel';

interface Lookup {
  readonly reference: PolicyReference;
  readonly damageDate: CivilDate;
}

/**
 * Neuer Schaden (SDD-018 §9, ui-spec §7): one page in v1's order — Police → Schadendatum →
 * Deckung → Versichertes Objekt → Schadensart → Überschrift / Beschreibung / Meldedatum /
 * Schadensenddatum → "Schaden anlegen".
 *
 * The policy is named by its code, or by the bike's plate or frame number AS IT WAS on the damage
 * date (the identifier lookup reads the slice valid that day; an ambiguous one lists the
 * candidates). The coverage answer is the gate (C-11): creating is offered only for `COVERED`, and
 * the claim types are the categories the variant covered that day. The new claim is linked to the
 * liable party — the policyholder, or for a member the group's — as "Versicherungsnehmer".
 */
export function ClaimCreatePage({
  initialPolicyCode = '',
  initialDamageDate = '',
}: {
  readonly initialPolicyCode?: string;
  readonly initialDamageDate?: string;
}): ReactNode {
  const { t, i18n } = useTranslation('claims');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const [mode, setMode] = useState<LookupMode>('policyCode');
  const [value, setValue] = useState(initialPolicyCode);
  const [damageDate, setDamageDate] = useState<string>(
    isCivilDate(initialDamageDate) ? initialDamageDate : '',
  );
  // A link that names both the policy and the day asks at once.
  const [lookup, setLookup] = useState<Lookup | null>(() => {
    const reference = policyReference('policyCode', initialPolicyCode);
    return reference !== undefined && isCivilDate(initialDamageDate)
      ? { reference, damageDate: initialDamageDate }
      : null;
  });
  const coverage = useCoverageAtDate({
    ...(lookup?.reference ?? {}),
    damageDate: lookup?.damageDate ?? '',
  });

  const reference = policyReference(mode, value);
  const canCheck = reference !== undefined && isCivilDate(damageDate);
  const check = (): void => {
    if (reference === undefined || !isCivilDate(damageDate)) return;
    setLookup({ reference, damageDate });
  };
  /** A changed input makes the answer stale: it is asked again, never reused for another policy. */
  const edited = (): void => {
    setLookup(null);
  };

  const candidates = coverage.isError ? ambiguousCodesOf(coverage.error) : undefined;
  const answer = lookup === null ? undefined : coverage.data;
  const verdict = answer === undefined ? undefined : verdictOf(answer);

  return (
    <PageLayout
      header={
        <PageHeader
          title={t('create.title')}
          description={t('create.subtitle')}
          breadcrumbs={[{ label: t('list.title'), to: links.claims.claims() }]}
        />
      }
    >
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
          <Stack spacing={2} component="section" aria-labelledby="claim-policy">
            <Typography variant="h3" component="h2" id="claim-policy">
              {t('create.policy.title')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              aria-label={t('create.policy.modeLabel')}
              onChange={(_event, next: LookupMode | null) => {
                if (next === null) return;
                setMode(next);
                edited();
              }}
            >
              {LOOKUP_MODES.map((item) => (
                <ToggleButton key={item} value={item}>
                  {t(`create.policy.mode.${item}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              sx={{ alignItems: { sm: 'flex-start' } }}
            >
              <TextField
                size="small"
                required
                label={t(`create.policy.input.${mode}`)}
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  edited();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') check();
                }}
                sx={{ minWidth: 260 }}
              />
              <TextField
                size="small"
                required
                type="date"
                label={t('create.damageDate')}
                value={damageDate}
                onChange={(event) => {
                  setDamageDate(event.target.value);
                  edited();
                }}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: civilToday() } }}
                helperText={t('create.damageDateHelp')}
              />
              <Button variant="outlined" onClick={check} disabled={!canCheck}>
                {t('create.check')}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {lookup !== null && coverage.isPending && (
          <Skeleton variant="rounded" height={160} data-testid="coverage-loading" />
        )}
        {lookup !== null && candidates !== undefined && (
          <Alert severity="warning" data-testid="coverage-ambiguous">
            <Stack spacing={1}>
              <span>{t('create.ambiguous', { count: candidates.length })}</span>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {candidates.map((code) => (
                  <Button
                    key={code}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setMode('policyCode');
                      setValue(code);
                      setLookup({ reference: { policyCode: code }, damageDate: lookup.damageDate });
                    }}
                  >
                    {code}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Alert>
        )}
        {lookup !== null && coverage.isError && candidates === undefined && (
          <ErrorState
            description={t('coverage.loadError')}
            details={coverage.error.message}
            onRetry={() => void coverage.refetch()}
          />
        )}
        {answer !== undefined && lookup !== null && (
          <CoveragePanel coverage={answer} damageDate={lookup.damageDate} locale={locale} />
        )}

        {answer !== undefined && lookup !== null && verdict === 'COVERED' ? (
          <ClaimForm
            key={`${answer.policy?.code ?? ''}:${lookup.damageDate}`}
            coverage={answer}
            damageDate={lookup.damageDate}
            onCreated={(code) => {
              void navigate({ href: links.claims.claim(code) });
            }}
          />
        ) : (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Button variant="contained" disabled>
              {t('create.submit')}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {t('create.blocked')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </PageLayout>
  );
}

/** The claim itself, once the coverage said COVERED. Keyed on policy + day: a new answer, a new form. */
function ClaimForm({
  coverage,
  damageDate,
  onCreated,
}: {
  readonly coverage: Coverage;
  readonly damageDate: CivilDate;
  readonly onCreated: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('claims');
  const today = civilToday();
  const policyCode = coverage.policy?.code ?? '';
  const objects = coverage.insuredObjects ?? [];
  const categories = offerableCategories(coverage);
  const roles = useClaimPartnerRoles(coverage.policy?.tarifSlug ?? '');
  const liableRole = (roles.data?.items ?? []).find((role) => role.name === LIABLE_ROLE_NAME);
  const create = useCreateClaim();
  const form = useForm<CreateClaimValues>({
    resolver: zodResolver(createClaimSchema({ today, damageDate })),
    defaultValues: {
      policyObjectCode:
        objects.length === 1 ? policyObjectCodeOf(policyCode, objects[0]?.name ?? '') : '',
      claimType: '',
      title: '',
      description: '',
      notificationDate: today,
      damageEndDate: '',
    },
  });
  const message = (key: string | undefined): string | undefined =>
    key === undefined ? undefined : t(key as never);

  const submit = form.handleSubmit((values) => {
    const partnerId = coverage.liableParty?.partnerId ?? '';
    create.mutate(
      {
        claim: toCreateRequest(values, policyCode, damageDate),
        ...(partnerId !== '' && liableRole !== undefined
          ? { liableParty: { partnerCode: partnerId, claimPartnerRoleCode: liableRole.code } }
          : {}),
      },
      {
        onSuccess: (created) => {
          onCreated(created.claim.code);
        },
      },
    );
  });

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <FormSection title={t('create.claim.title')} description={t('create.claim.description')}>
        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          <Controller
            control={form.control}
            name="policyObjectCode"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                size="small"
                required
                label={t('create.object')}
                error={fieldState.error !== undefined}
                helperText={message(fieldState.error?.message)}
              >
                {objects.map((object) => (
                  <MenuItem
                    key={object.name}
                    value={policyObjectCodeOf(policyCode, object.name ?? '')}
                  >
                    {object.label === undefined || object.label === '' ? object.name : object.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="claimType"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                size="small"
                required
                label={t('create.claimType')}
                error={fieldState.error !== undefined}
                helperText={message(fieldState.error?.message) ?? t('create.claimTypeHelp')}
              >
                {categories.map((category) => (
                  <MenuItem key={category.key} value={category.key}>
                    {categoryLabel(category)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                required
                label={t('create.claimTitle')}
                error={fieldState.error !== undefined}
                helperText={message(fieldState.error?.message)}
              />
            )}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                multiline
                minRows={3}
                label={t('create.description')}
              />
            )}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              control={form.control}
              name="notificationDate"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  required
                  type="date"
                  label={t('create.notificationDate')}
                  error={fieldState.error !== undefined}
                  helperText={message(fieldState.error?.message)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
            <Controller
              control={form.control}
              name="damageEndDate"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  type="date"
                  label={t('create.damageEndDate')}
                  error={fieldState.error !== undefined}
                  helperText={message(fieldState.error?.message)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Stack>
          {create.isError && (
            <Alert severity="error" data-testid="create-error">
              {t('create.error')}
            </Alert>
          )}
          {roles.isSuccess && liableRole === undefined && (
            <Alert severity="info">{t('create.noLiableRole')}</Alert>
          )}
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" disabled={create.isPending}>
              {t('create.submit')}
            </Button>
          </Stack>
        </Stack>
      </FormSection>
    </form>
  );
}
